import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  collection, 
  getDocs, 
  getDoc, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  limit
} from 'firebase/firestore';
import { db } from '../../firebase/config';

// Async thunks
export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async ({ userId, limit: queryLimit = 50 } = {}, { rejectWithValue }) => {
    try {
      // If no userId provided, reject
      if (!userId) {
        return rejectWithValue('Benutzer-ID erforderlich');
      }
      
      const notificationsQuery = query(
        collection(db, 'notifications'),
        where('user_id', '==', userId),
        orderBy('created_at', 'desc'),
        limit(queryLimit)
      );
      
      const querySnapshot = await getDocs(notificationsQuery);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createNotification = createAsyncThunk(
  'notifications/createNotification',
  async (notificationData, { rejectWithValue }) => {
    try {
      const { user_id, title, message, type, data = {} } = notificationData;
      
      const newNotification = {
        user_id,
        title,
        message,
        type,
        is_read: false,
        created_at: new Date().toISOString(),
        data
      };
      
      const docRef = await addDoc(collection(db, 'notifications'), newNotification);
      
      return {
        id: docRef.id,
        ...newNotification
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const markNotificationAsRead = createAsyncThunk(
  'notifications/markAsRead',
  async (notificationId, { rejectWithValue }) => {
    try {
      const notificationRef = doc(db, 'notifications', notificationId);
      
      await updateDoc(notificationRef, {
        is_read: true,
        updated_at: new Date().toISOString()
      });
      
      return notificationId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const markAllNotificationsAsRead = createAsyncThunk(
  'notifications/markAllAsRead',
  async (userId, { rejectWithValue, getState }) => {
    try {
      const { notifications } = getState().notifications;
      const unreadNotifications = notifications.filter(notification => 
        !notification.is_read && notification.user_id === userId
      );
      
      const updatePromises = unreadNotifications.map(notification => 
        updateDoc(doc(db, 'notifications', notification.id), {
          is_read: true,
          updated_at: new Date().toISOString()
        })
      );
      
      await Promise.all(updatePromises);
      
      return unreadNotifications.map(notification => notification.id);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteNotification = createAsyncThunk(
  'notifications/deleteNotification',
  async (notificationId, { rejectWithValue }) => {
    try {
      await deleteDoc(doc(db, 'notifications', notificationId));
      return notificationId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteAllNotifications = createAsyncThunk(
  'notifications/deleteAll',
  async (userId, { rejectWithValue, getState }) => {
    try {
      const { notifications } = getState().notifications;
      const userNotifications = notifications.filter(notification => 
        notification.user_id === userId
      );
      
      const deletePromises = userNotifications.map(notification => 
        deleteDoc(doc(db, 'notifications', notification.id))
      );
      
      await Promise.all(deletePromises);
      
      return userNotifications.map(notification => notification.id);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  notifications: [],
  unreadCount: 0,
  overdueRidesCount: 0, // Separate Zählung für überfällige Fahrten
  isLoading: false,
  error: null
};

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    clearNotificationsError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch notifications
      .addCase(fetchNotifications.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.isLoading = false;
        state.notifications = action.payload;
        state.unreadCount = action.payload.filter(notification => !notification.is_read).length;
        state.overdueRidesCount = action.payload.filter(notification => 
          !notification.is_read && notification.type === 'overdue_ride'
        ).length;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Create notification
      .addCase(createNotification.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createNotification.fulfilled, (state, action) => {
        state.isLoading = false;
        state.notifications.unshift(action.payload);
        state.unreadCount += 1;
        if (action.payload.type === 'overdue_ride') {
          state.overdueRidesCount += 1;
        }
      })
      .addCase(createNotification.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Mark notification as read
      .addCase(markNotificationAsRead.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        state.isLoading = false;
        const notification = state.notifications.find(n => n.id === action.payload);
        if (notification && !notification.is_read) {
          notification.is_read = true;
          state.unreadCount -= 1;
          if (notification.type === 'overdue_ride') {
            state.overdueRidesCount -= 1;
          }
        }
      })
      .addCase(markNotificationAsRead.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Mark all notifications as read
      .addCase(markAllNotificationsAsRead.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(markAllNotificationsAsRead.fulfilled, (state, action) => {
        state.isLoading = false;
        action.payload.forEach(id => {
          const notification = state.notifications.find(n => n.id === id);
          if (notification) {
            notification.is_read = true;
          }
        });
        state.unreadCount = state.notifications.filter(n => !n.is_read).length;
        state.overdueRidesCount = state.notifications.filter(n => 
          !n.is_read && n.type === 'overdue_ride'
        ).length;
      })
      .addCase(markAllNotificationsAsRead.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete notification
      .addCase(deleteNotification.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteNotification.fulfilled, (state, action) => {
        state.isLoading = false;
        const deletedNotification = state.notifications.find(n => n.id === action.payload);
        
        state.notifications = state.notifications.filter(
          notification => notification.id !== action.payload
        );
        
        if (deletedNotification && !deletedNotification.is_read) {
          state.unreadCount -= 1;
          if (deletedNotification.type === 'overdue_ride') {
            state.overdueRidesCount -= 1;
          }
        }
      })
      .addCase(deleteNotification.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete all notifications
      .addCase(deleteAllNotifications.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteAllNotifications.fulfilled, (state, action) => {
        state.isLoading = false;
        state.notifications = state.notifications.filter(
          notification => !action.payload.includes(notification.id)
        );
        state.unreadCount = state.notifications.filter(n => !n.is_read).length;
        state.overdueRidesCount = state.notifications.filter(n => 
          !n.is_read && n.type === 'overdue_ride'
        ).length;
      })
      .addCase(deleteAllNotifications.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  }
});

export const { clearNotificationsError } = notificationsSlice.actions;
export default notificationsSlice.reducer;